import { useState, useMemo } from "react";
import { Popover, IconButton, Flex, Text, Callout } from "@radix-ui/themes";
import {
  InfoCircledIcon,
  Cross2Icon,
  ExclamationTriangleIcon,
} from "@radix-ui/react-icons";
import { useSystemTime } from "@/features/annotation/hooks/useSystemTime";
import { useAppSelector } from "@/app/appStore";
import { selectDateRange, selectSelectedOrder } from "@/entities/annotation";
import { InfoItem } from "@/components/info-item";
import { formatTimeDifference } from "../lib/helpers";

interface SystemTimeInfoPopoverProps {
  className?: string;
}

interface SystemStatusInfo {
  status: SystemStatus;
  message: string;
  color: "red" | "gray" | "blue" | "green";
}

const THIRTY_MINUTES = 30;
const DEFAULT_ERROR_CODE = 500;

enum SystemStatus {
  ERROR = "error",
  UNKNOWN = "unknown",
  AHEAD_OF_RANGE = "ahead_of_range",
  BEHIND_30MIN = "behind_30min",
  BEHIND = "behind",
  AHEAD = "ahead",
  EXACT = "exact",
}

const createErrorMessage = (error: any, retrySeconds: number): string => {
  const hasDetail = error?.data?.detail;
  const errorCode = error?.status || DEFAULT_ERROR_CODE;
  const errorDetail = hasDetail ? error.data.detail : "Error message here";

  return `Failed to fetch AI system info.\nRetrying in: ${retrySeconds} s\nError code: ${errorCode}\n${errorDetail}`;
};

interface StatusIconProps {
  status: SystemStatus;
}

const StatusIcon = ({ status }: StatusIconProps) => {
  const isCriticalStatus =
    status === SystemStatus.ERROR || status === SystemStatus.BEHIND_30MIN;
  return isCriticalStatus ? (
    <ExclamationTriangleIcon color="red" />
  ) : (
    <InfoCircledIcon color="gray" />
  );
};

const SystemTimeInfo = ({
  systemDateTime,
  isSystemTimeLoading,
  formattedTimeElapsed,
}: {
  isSystemTimeLoading: boolean;
  formattedTimeElapsed: string;
  systemDateTime?: string;
}) => (
  <Flex direction="column" gap="1">
    <InfoItem
      label="AI system time"
      value={isSystemTimeLoading ? "Loading..." : systemDateTime || "-"}
      copyValue={systemDateTime}
    />
    <InfoItem label="Last updated" value={`${formattedTimeElapsed} ago`} />
    <Flex className="py-1">
      <Text size="1" color="gray" className="min-w-fit">
        Updated every 30s
      </Text>
    </Flex>
  </Flex>
);

const StatusCallout = ({ statusInfo }: { statusInfo: SystemStatusInfo }) => (
  <Callout.Root color={statusInfo.color}>
    <Callout.Icon>
      <StatusIcon status={statusInfo.status} />
    </Callout.Icon>
    <Callout.Text>
      {statusInfo.message.split("\n").map((line, index) => (
        <Text key={index} size="1" style={{ display: "block" }}>
          {line}
        </Text>
      ))}
    </Callout.Text>
  </Callout.Root>
);

export const SystemTimeInfoPopover = ({
  className,
}: SystemTimeInfoPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const dateRange = useAppSelector(selectDateRange);

  const {
    data: systemTimeData,
    isLoading: isSystemTimeLoading,
    isError: isSystemTimeError,
    error: systemTimeError,
    formattedTimeElapsed,
    retrySeconds,
  } = useSystemTime();

  const systemDateTime = systemTimeData?.system_datetime;

  const statusInfo = useMemo((): SystemStatusInfo => {
    if (isSystemTimeError) {
      return {
        status: SystemStatus.ERROR,
        message: createErrorMessage(systemTimeError, retrySeconds),
        color: "red",
      };
    }

    if (!systemDateTime || !selectedOrder?.time) {
      return {
        status: SystemStatus.UNKNOWN,
        message: "No data available",
        color: "gray",
      };
    }

    const systemTime = new Date(systemDateTime);
    const orderTime = new Date(selectedOrder.time);
    const timeDiff = systemTime.getTime() - orderTime.getTime();
    const diffInMinutes = Math.floor(timeDiff / (1000 * 60));

    if (dateRange.end && systemTime.getTime() > dateRange.end.getTime()) {
      return {
        status: SystemStatus.AHEAD_OF_RANGE,
        message: "System is ahead of selected time range.",
        color: "gray",
      };
    }

    if (diffInMinutes < -THIRTY_MINUTES) {
      return {
        status: SystemStatus.BEHIND_30MIN,
        message: `System is ${formatTimeDifference(timeDiff)} behind order creation time.`,
        color: "red",
      };
    }

    if (timeDiff < 0) {
      return {
        status: SystemStatus.BEHIND,
        message: `System is ${formatTimeDifference(timeDiff)} behind order creation time.`,
        color: "gray",
      };
    }

    if (timeDiff > 0) {
      return {
        status: SystemStatus.AHEAD,
        message: `System is ${formatTimeDifference(timeDiff)} ahead of order creation time.`,
        color: "gray",
      };
    }

    return {
      status: SystemStatus.EXACT,
      message: "System time matches order creation time.",
      color: "gray",
    };
  }, [
    isSystemTimeError,
    systemTimeError,
    systemDateTime,
    selectedOrder?.time,
    dateRange.end,
    retrySeconds,
  ]);

  const handleClose = () => setIsOpen(false);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <IconButton
          variant="ghost"
          color="gray"
          size="2"
          className={className}
          title="AI System time info"
        >
          <StatusIcon status={statusInfo.status} />
        </IconButton>
      </Popover.Trigger>

      <Popover.Content maxWidth="350px" align="start">
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center">
            <Text size="2" weight="bold">
              AI System Info
            </Text>
            <IconButton
              variant="ghost"
              highContrast
              color="gray"
              size="2"
              onClick={handleClose}
            >
              <Cross2Icon />
            </IconButton>
          </Flex>

          <SystemTimeInfo
            systemDateTime={systemDateTime}
            isSystemTimeLoading={isSystemTimeLoading}
            formattedTimeElapsed={formattedTimeElapsed}
          />

          <StatusCallout statusInfo={statusInfo} />
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
};
